// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/** DOM plumbing: modals, the drop zone, panel switching and the page strip. */

import { formatBytes, formatDimensions, pluralise } from './format';
import type { ScanPage } from './types';

export function must<T extends HTMLElement>(id: string): T {
  const el = document.getElementById(id);
  if (!el) throw new Error(`Missing element #${id}`);
  return el as T;
}

// ──────────────────────────────────────────────────────────── modals ──

let openModal: HTMLElement | null = null;

function buildModal(templateId: string): HTMLElement {
  const template = document.getElementById(templateId) as HTMLTemplateElement | null;
  if (!template) throw new Error(`Missing template #${templateId}`);

  const dialog = document.createElement('div');
  dialog.className = 'modal';
  dialog.setAttribute('role', 'dialog');
  dialog.setAttribute('aria-modal', 'true');

  const close = document.createElement('button');
  close.type = 'button';
  close.className = 'modal-close';
  close.setAttribute('aria-label', 'Close');
  close.textContent = '×';
  close.addEventListener('click', () => closeModal());

  const body = document.createElement('div');
  body.className = 'modal-body';
  body.append(template.content.cloneNode(true));

  dialog.append(close, body);
  return dialog;
}

export function closeModal(): void {
  openModal?.remove();
  openModal = null;
  must('modal-backdrop').hidden = true;
  document.body.classList.remove('modal-open');
}

export function showModal(name: string): void {
  closeModal();
  const dialog = buildModal(`modal-${name}`);
  must('modal-backdrop').hidden = false;
  document.body.append(dialog);
  document.body.classList.add('modal-open');
  openModal = dialog;
  (dialog.querySelector('.modal-close') as HTMLElement | null)?.focus();
}

export function isModalOpen(): boolean {
  return openModal !== null;
}

export function initModals(): void {
  document.addEventListener('click', (event) => {
    const trigger = (event.target as HTMLElement | null)?.closest<HTMLElement>('[data-modal]');
    if (trigger?.dataset.modal) {
      event.preventDefault();
      showModal(trigger.dataset.modal);
    }
  });

  must('modal-backdrop').addEventListener('click', () => closeModal());

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && openModal) closeModal();
  });
}

// ───────────────────────────────────────────────────────── drop zone ──

export function isSupportedImage(file: File): boolean {
  return file.type.startsWith('image/');
}

/**
 * Wires drag-and-drop, click-to-pick and paste-to-ingest onto the drop zone.
 * Returns a teardown function so the paste listener doesn't outlive the page.
 */
export function initDropzone(onFiles: (files: File[]) => void): () => void {
  const zone = must('dropzone');
  const input = must<HTMLInputElement>('file-input');

  const accept = (list: FileList | null | undefined) => {
    const files = Array.from(list ?? []).filter(isSupportedImage);
    if (files.length) onFiles(files);
  };

  const pick = () => input.click();
  zone.addEventListener('click', pick);
  zone.addEventListener('keydown', (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      pick();
    }
  });

  input.addEventListener('change', () => {
    accept(input.files);
    // Reset so picking the same file twice still fires a change event.
    input.value = '';
  });

  const stop = (event: DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
  };

  zone.addEventListener('dragover', (event) => {
    stop(event);
    zone.classList.add('is-dragging');
  });
  zone.addEventListener('dragleave', (event) => {
    stop(event);
    zone.classList.remove('is-dragging');
  });
  zone.addEventListener('drop', (event) => {
    stop(event);
    zone.classList.remove('is-dragging');
    accept(event.dataTransfer?.files);
  });

  // Dropping anywhere else on the page shouldn't navigate away from the app.
  const blockNavigation = (event: DragEvent) => event.preventDefault();
  window.addEventListener('dragover', blockNavigation);
  window.addEventListener('drop', blockNavigation);

  const onPaste = (event: ClipboardEvent) => {
    const files = Array.from(event.clipboardData?.items ?? [])
      .filter((item) => item.type.startsWith('image/'))
      .map((item) => item.getAsFile())
      .filter((file): file is File => file !== null);
    if (files.length) onFiles(files);
  };
  document.addEventListener('paste', onPaste);

  return () => {
    document.removeEventListener('paste', onPaste);
    window.removeEventListener('dragover', blockNavigation);
    window.removeEventListener('drop', blockNavigation);
  };
}

// ──────────────────────────────────────────────────── panel switching ──

export type Panel = 'intake' | 'camera' | 'editor' | 'progress' | 'result';

export function showPanel(panel: Panel): void {
  must('intake').hidden = panel !== 'intake';
  must('camera-panel').hidden = panel !== 'camera';
  must('editor').hidden = panel !== 'editor';
  must('progress-panel').hidden = panel !== 'progress';
  must('result-panel').hidden = panel !== 'result';
}

export function setProgress(phase: string, ratio: number, detail?: string): void {
  const percent = Math.round(Math.min(Math.max(ratio, 0), 1) * 100);
  must('progress-phase').textContent = phase;
  must('progress-fill').style.width = `${percent}%`;
  must('progress-detail').textContent = detail ?? `${percent}%`;
}

export function showError(message: string): void {
  must('error-message').textContent = message;
  must('error-banner').hidden = false;
}

export function clearError(): void {
  must('error-banner').hidden = true;
}

// ───────────────────────────────────────────────────────── page strip ──

export interface PageStripHandlers {
  onRemove: (id: string) => void;
  onMove: (id: string, delta: number) => void;
  onCopy: (id: string) => void;
}

/** Re-renders the whole strip. It's at most a few dozen nodes. */
export function renderPageStrip(
  pages: readonly ScanPage[],
  handlers: PageStripHandlers,
  canCopy: boolean,
): void {
  const strip = must<HTMLUListElement>('page-strip');
  strip.replaceChildren();

  pages.forEach((page, index) => {
    const item = document.createElement('li');
    item.className = 'page-card';

    const image = document.createElement('img');
    image.src = page.url;
    image.alt = `Page ${index + 1}`;
    image.loading = 'lazy';

    const number = document.createElement('span');
    number.className = 'page-number';
    number.textContent = String(index + 1);

    const meta = document.createElement('p');
    meta.className = 'page-meta';
    meta.textContent = `${formatDimensions(page.width, page.height)} · ${formatBytes(page.blob.size)}`;

    const actions = document.createElement('div');
    actions.className = 'page-actions';

    const button = (label: string, aria: string, onClick: () => void, disabled = false) => {
      const el = document.createElement('button');
      el.type = 'button';
      el.className = 'page-action';
      el.textContent = label;
      el.setAttribute('aria-label', `${aria}, page ${index + 1}`);
      el.disabled = disabled;
      el.addEventListener('click', onClick);
      return el;
    };

    actions.append(
      button('◀', 'Move earlier', () => handlers.onMove(page.id, -1), index === 0),
      button('▶', 'Move later', () => handlers.onMove(page.id, 1), index === pages.length - 1),
    );
    if (canCopy) {
      actions.append(button('Copy', 'Copy to clipboard', () => handlers.onCopy(page.id)));
    }
    actions.append(button('×', 'Remove', () => handlers.onRemove(page.id)));

    item.append(number, image, meta, actions);
    strip.append(item);
  });

  must('result-title').textContent = `${pluralise(pages.length, 'page')} ready`;
}
